<template>
  <main
    v-if="status"
    :id="$route.name"
    ref="candidateRoot"
    :class="candidate.color"
  >
    <header class="site-header">
      <div class="container">
        <div class="site-label">
          <h1>
            {{ candidate.popular_name }}
          </h1>
          <h2>
            <template v-if="candidate.campaign_donation_type === 'campaign'">
              Candidato(a)
            </template>
            <template v-else>
              Pré-candidato(a)
            </template>
            a {{ offices[candidate.office_id] }}
          </h2>
          <ul class="wrap-social-candidate">
            <li v-if="candidate.facebook_url">
              <a
                :href="candidate.facebook_url"
                target="_blank"
                class="facebook"
              />
            </li>
            <li v-if="candidate.twitter_url">
              <a
                :href="candidate.twitter_url"
                target="_blank"
                class="twitter"
              />
            </li>
            <li v-if="candidate.instagram_url">
              <a
                :href="candidate.instagram_url"
                target="_blank"
                class="instagram"
              />
            </li>
            <li v-if="candidate.whatsapp_number">
              <a
                :href="`https://api.whatsapp.com/send?phone=${candidate.whatsapp_number}`"
                target="_blank"
                class="whatsapp"
              />
            </li>
            <li v-if="candidate.youtube_url">
              <a
                :href="candidate.youtube_url"
                target="_blank"
                class="youtube"
              />
            </li>
            <li v-if="candidate.website_url">
              <a
                :href="candidate.website_url"
                target="_blank"
                class="website"
              />
            </li>
            <li v-if="candidate.public_email">
              <a
                :href="`mailto:${candidate.public_email}`"
                class="email"
              />
            </li>
          </ul>
        </div>
      </div>
    </header>

    <article role="main">
      <div class="container">
        <div class="candidate-wrap">
          <header id="intro">
            <figure>
              <!-- if candidate.hasVideo -->
              <div class="embed-container">
                <iframe
                  v-if="videoId"
                  width="560"
                  height="315"
                  :src="`https://www.youtube-nocookie.com/embed/${videoId}`"
                  frameborder="0"
                  allow="autoplay; encrypted-media"
                  allowfullscreen
                />
              </div>
              <!-- else -->
              <!-- <img src="[[ source ]]" alt="[[ i18n pictureOf ]] [[ candidate.name ]]" srcset="" width="560" height="315"> -->
              <!-- end if -->
            </figure>
            <dl class="intro__meta">
              <div class="intro__meta-item">
                <dt>{{ 'party' | translate }}</dt>
                <dd v-if="candidate.party">
                  <abbr :title="candidate.party.name">
                    {{ candidate.party.acronym }}
                  </abbr>
                </dd>
              </div>

              <div
                v-if="candidate.number"
                class="intro__meta-item"
              >
                <dt>{{ 'number' | translate }}</dt>
                <dd>{{ candidate.number }}</dd>
              </div>

              <div
                v-if="candidate.address_state_name"
                class="intro__meta-item"
              >
                <dt>{{ 'runingon' | translate }}</dt>
                <dd>
                  <span class="locality">{{ candidate.address_city }}</span>
                  -
                  <span class="region">{{ candidate.address_state_name }}</span>
                </dd>
              </div>
              <div
                v-if="candidate.political_movement_name"
                class="intro__meta-item"
              >
                <dt>{{ 'politicalMovement' | translate }}</dt>
                <dd>{{ candidate.political_movement_name }}</dd>
              </div>
            </dl>
          </header>
          <section id="campaign-progress">
            <dl class="campaign-progress__meta">
              <div class="campaign-progress__meta-item campaign-progress__meta-item--expected">
                <dd>
                  <div
                    v-if="donationSources.length > 1"
                    role="progressbar"
                    aria-valuemin="0"
                    :aria-valuenow="totalAmount"
                    :aria-valuemax="goal"
                  >
                    <template v-for="(source, i) in donationSources">
                      <div
                        v-if="source.total_donated"
                        :key="i"
                        :style="progressBarStyle(source)"
                        :title="`${porcentage(source.total_donated)}% via ${source.name}`"
                      >
                        {{ porcentage(source.total_donated) }}
                      </div>
                    </template>
                  </div>
                  <progress
                    v-else
                    :value="totalAmount"
                    :max="goal"
                  >
                    <div
                      role="progressbar"
                      :aria-valuenow="totalAmount"
                      :aria-valuemax="goal"
                    >
                      <div :style="{width: `${porcentage(totalAmount)}%`}">
                        {{ porcentage(totalAmount) }}
                      </div>
                    </div>
                  </progress>
                </dd>
                <dt>
                  <span class="campaign-progress__meta-currency">
                    {{ 'currencyReal' | translate }}
                  </span>
                  <span v-if="totalAmount">
                    <animated-number
                      :value="totalAmount"
                      :format-value="FormatFixedBRL"
                      :duration="1000"
                    />
                  </span>
                  <span
                    v-if="'capturedMoney'"
                    class="campaign-progress__meta-captured"
                  >
                    {{ 'capturedMoney' | translate }}
                  </span>
                </dt>
                <dd>
                  {{ 'gotFromTheExpected' | translate | lowerCase }}	{{ 'currencyReal' | translate }}
                  <strong
                    v-if="goal"
                    class="campaign-progress__expected-value"
                  >
                    {{ goal | formatBRL }}
                  </strong>
                </dd>
                <dd>
                  <div
                    v-if="donationSources.length > 1"
                    class="donations-sources"
                  >
                    <ul>
                      <li
                        v-for="(source, i) in donationSources"
                        :key="i"
                      >
                        <strong class="donations-sources__amount">
                          R$ {{ source.total_donated | formatBRL }}
                        </strong> /
                        <em class="donations-sources__donations">
                          {{ source.people_donated }}
                        </em>
                        doações via {{ source.name }}
                      </li>
                    </ul>
                  </div>
                </dd>
              </div>
              <div class="campaign-progress__meta-item">
                <dt>{{ totalDonors }}</dt>
                <dd>{{ 'doners' | translate | lowerCase }}</dd>
              </div>
              <div
                v-if="candidate.ramaining_days"
                class="campaign-progress__meta-item"
              >
                <dt>{{ candidate.ramaining_days }}</dt>
                <dd>{{ 'remainingDays' | translate | lowerCase }}</dd>
              </div>
            </dl>
          </section>
        </div>

        <div class="candidate-content">
          <section id="doar">
            <Payment />
          </section>
          <section
            v-if="candidate.rede_ticket"
            id="votar"
          >
            <Ticket />
          </section>

          <div class="entry-contents">
            <section
              v-if="candidate.summary"
              class="candidate-summary"
            >
              <p>{{ candidate.summary }}</p>
            </section>

            <nav
              v-if="!isCrawler"
              class="entry-content__nav"
            >
              <ul>
                <li
                  v-if="getMergeCandidate.projects.length"
                  :class="`${activeSection === 'projetos' ? 'active-nav-item' : ''}`"
                >
                  <a
                    href="#projetos"
                    @click.prevent="changeSection('projetos')"
                  >{{ 'mainProjects' | translate }}</a>
                </li>
                <li
                  v-if="candidate.biography"
                  :class="`${activeSection === 'sobre' ? 'active-nav-item' : ''}`"
                >
                  <a
                    href="#sobre"
                    @click.prevent="changeSection('sobre')"
                  >
                    {{ 'aboutMe' | translate }}
                  </a>
                </li>
                <li
                  v-if="donations && candidate.party"
                  :class="`${activeSection === 'doacoes' ? 'active-nav-item' : ''}`"
                >
                  <a
                    href="#doacoes"
                    @click.prevent="changeSection('doacoes')"
                    @click.prevent.once="getDonationsList()"
                  >
                    {{ 'donations' | translate }}
                  </a>
                </li>
                <li
                  v-if="tickets && candidate.rede_ticket"
                  :class="`${activeSection === 'vales' ? 'active-nav-item' : ''}`"
                >
                  <a
                    href="#vales"
                    @click.prevent="changeSection('vales')"
                    @click.prevent.once="getTicketsList()"
                  >
                    {{ 'ticket' | translate }}
                  </a>
                </li>
              </ul>
            </nav>

            <section
              v-if="getMergeCandidate.projects.length"
              id="projetos"
              :class="`entry-content ${activeSection === 'projetos' ? 'entry-content-active' : ''}`"
            >
              <h2>{{ 'mainProjects' | translate }}</h2>
              <div
                v-for="projects in getMergeCandidate.projects"
                :key="projects.id"
              >
                <h3>{{ projects.title }}</h3>
                <p>{{ projects.scope }}</p>
              </div>
            </section>
            <section
              id="sobre"
              :class="`entry-content ${activeSection === 'sobre' ? 'entry-content-active' : ''}`"
            >
              <h2>{{ 'aboutMe' | translate }}</h2>
              <p class="candidate-biography">
                {{ candidate.biography }}
              </p>

              <p
                v-if="candidate.cnpj"
                class="candidate-cnpj"
              >
                <strong>
                  <abbr title="Cadastro Nacional de Pessoa Jurídica">CNPJ</abbr>
                  da campanha:
                </strong>
                {{ candidate.cnpj | formatCNPJ }}
              </p>
            </section>
            <section
              v-if="donations && candidate.party"
              id="doacoes"
              :class="`entry-content ${activeSection === 'doacoes' ? 'entry-content-active' : ''}`"
            >
              <h2>{{ 'donations' | translate }}</h2>
              <h4>O que são os comprovantes?</h4>
              <p>
                O <template v-if="candidate.campaign_donation_type === 'campaign'">
                  candidato(a)
                </template><template v-else>
                  pré-candidato(a)
                </template> registra todas suas doações em um cartório cívico digital, para comprovar e manter a autenticidade da sua doação. *Essa rede de informações é vinculada à uma das mais seguras e confiáveis do mundo, que é a blockchain do DECRED. Esses números são a referência sobre as doações realizadas.
              </p>

              <h4>Por que isso é importante?</h4>

              <p>As doações eleitorais são muito importantes para construção de projetos políticos. Porém, é fundamental que as plataformas de financiamento coletivo, sociedade e o TSE tenham garantias que as informações ofereçam integridade e confiabilidade de maneira descentralizada para garantir controle social.</p>


              <p>
                Por isso a <template v-if="candidate.campaign_donation_type === 'campaign'">
                  candidatura
                </template><template v-else>
                  pré-candidatura
                </template> da(o) {{ candidate.popular_name }}, do {{ candidate.party.name.toLowerCase().indexOf('partido') === -1 ? 'Partido' : '' }} {{ candidate.party.name }}, está registrando todas suas doações financeiras, para comprovar a integridade e honestidade do processo de captação de recursos.
              </p>

              <table
                v-if="donations.length > 0"
                class="donations-table"
              >
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>CPF</th>
                    <th>Data</th>
                    <th>Método</th>
                    <th>Valor</th>
                    <th>Recibo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="donation in donations"
                    :key="donation.id"
                    :class="{irregular: donation.is_irregular}"
                  >
                    <th title="Nome">
                      {{ donation.name | titleCase }}
                    </th>
                    <td title="CPF">
                        <div v-html="$options.filters.formatCPF(donation.cpf)"></div>
                    </td>
                    <td title="Data">
                      {{ donation.captured_at | date }}
                    </td>
                    <td title="Método">
                      {{ donation.payment_method_human }}
                    </td>
                    <td title="Valor">
                      R$ {{ donation.amount | formatBRL }}
                    </td>
                    <td
                      v-if="donation.digest"
                      title="Recibo"
                    >
                      <a
                        :href="'/'+ candidate.username +'/recibo/' + donation.digest"
                        target="_blank"
                        title="Registro na blockchain"
                      ><i class="fa fa-link" /></a>
                    </td>
                    <td
                      v-else-if="!donation.platform || donation.platform === 'votolegal' || donation.platform === '30217474000150'"
                      title="Recibo"
                    >
                      Processando
                    </td>
                    <td
                      v-else-if="donation.platform"
                      title="Recibo"
                    >
                      {{ getPlatformName(donation.platform) }}
                    </td>
                    <td
                      v-else
                      title="Recibo"
                    />
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <th>Nome</th>
                    <th>CPF</th>
                    <th>Data</th>
                    <th>Método</th>
                    <th>Valor</th>
                    <th>Recibo</th>
                  </tr>
                </tfoot>
              </table>
              <h3 v-else>
                Ainda não há doações
              </h3>

              <button
                v-if="hasMoreDonations"
                class="button--load-more"
                type="button"
                @click.prevent="getDonationsList()"
              >
                {{ 'loadMore' | translate }}
              </button>
            </section>

            <section
              v-if="tickets && candidate.rede_ticket"
              id="vales"
              :class="`entry-content ${activeSection === 'vales' ? 'entry-content-active' : ''}`"
            >
              <h2>{{ 'ticket' | translate }}</h2>
              <h4>O que são os vales eleitorais?</h4>

              <p>
                Você sabe como os partidos políticos definem quais candidatos
                irão ganhar mais dinheiros e quais menos? Aqui na Rede
                Sustentabilidade da cidade de São Paulo a gente faz essa escolha
                com você!
              </p>

              <p>
                Para você participar da escolha de destinar parte do fundo
                eleitoral para candidaturas do {{
                  candidate.party.name.toLowerCase().indexOf('partido') === -1 ?
                    'Partido' : '' }} {{ candidate.party.name }} ({{
                  candidate.address_city }}), basta acessar a página do seu
                pré-candidato do VOTO LEGAL e clicar em "vale eleitoral" e
                preencher com seus dados para confirmar sua escolha. Você só
                pode destinar o vale para 1 pré-candidato ou pré-candidata e tem
                até o dia 27 de setembro para fazer isso.
              </p>

              <p>
                Aqui você pode acompanhar todos os vales registrados para a
                pré-candidatura de {{ candidate.popular_name }}, do {{
                  candidate.party.name.toLowerCase().indexOf('partido') === -1 ?
                    'Partido' : '' }} {{ candidate.party.name }}.
              </p>

              <table
                v-if="tickets.length > 0"
                class="tickets-table"
              >
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Data</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="ticket in tickets"
                    :key="ticket.id"
                    :class="{irregular: ticket.is_irregular}"
                  >
                    <th title="Nome">
                      {{ ticket.nome | titleCase }}
                    </th>
                    <td title="Data">
                      {{ ticket.criado_em | date }}
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <th>Nome</th>
                    <th>Data</th>
                  </tr>
                </tfoot>
              </table>
              <h3 v-else>
                Ainda não há vales
              </h3>

              <button
                v-if="hasMoreTickets"
                class="button--load-more"
                type="button"
                @click.prevent="getTicketsList()"
              >
                {{ 'loadMore' | translate }}
              </button>
            </section>
          </div>
        </div>
      </div>

      <!-- Facebook Chat Code -->
      <div
        v-if="candidate.chat && candidate.chat.page_id"
        class="fb-customerchat"
        attribution="setup_tool"
        :page_id="candidate.chat.page_id"
        :theme_color="colors[candidate.color]"
        :logged_in_greeting="candidate.chat.logged_in_greeting ? candidate.chat.logged_in_greeting : 'Posso ajudar?'"
        :logged_out_greeting="candidate.chat.logged_in_greeting ? candidate.chat.logged_in_greeting : 'Posso ajudar?'"
      />
    </article>

    <template
      v-if="candidate.whatsapp_number"
    >
      <a
        :href="`https://api.whatsapp.com/send?phone=${candidate.whatsapp_number}`"
        style="position:fixed;width:60px;height:60px;bottom:40px;right:40px;background-color:#25d366;color:#FFF;border-radius:50px;text-align:center;font-size:30px;
				box-shadow: 1px 1px 2px #888; z-index:1000; text-decoration: none;"
        target="_blank"
      >
        <i
          style="margin-top:16px"
          class="fa fa-whatsapp"
        />
      </a>
      <link
        rel="stylesheet"
        href="https://maxcdn.bootstrapcdn.com/font-awesome/4.5.0/css/font-awesome.min.css"
      >
    </template>
  </main>
  <main v-else>
    <header class="site-header">
      <div class="container">
        <div class="site-label">
          <h1>
            {{ errorMessage }}
          </h1>
        </div>
      </div>
    </header>
  </main>
</template>

<script>
	import {
		mapState,
		mapGetters,
		mapMutations
	} from 'vuex';

	import {
		VMoney
	} from 'v-money';

	import VotolegalFP from '../../vendor/loadme.js';
	import Payment from '../Payment.vue';
	import Ticket from '../Ticket.vue';
	import pagination from '../pagination/pagination.vue';
	import AnimatedNumber from "animated-number-vue";
	import { getQueryString } from "../../utilities";

	export default {
		name: 'Candidate',
		directives: {
			money: VMoney,
		},
		components: {
			Payment,
			Ticket,
			AnimatedNumber
		},
		data() {
			return {
				money: {
					decimal: ',',
					thousands: '.',
					prefix: 'R$ ',
					suffix: '',
					precision: 2,
				},
				videoId: '',
				status: true,
				errorMessage: '',
				teste: '',
				activeSection: 'projetos',
				colors: {
					'default': '#ff6633',
					'theme--default': '#ff6633',
					'theme--orange': '#ff6633',
					'theme--red': '#ed1941',
					'theme--blue': '#0072bc',
					'theme--green': '#339966',
				},
				defaultDescription: 'Voto Legal - Transparência e segurança em doações eleitorais',
			}
		},

		computed: {
			isCrawler() {
				return /bot|google|baidu|bing|msn|teoma|slurp|yandex/i
						.test(navigator.userAgent);
			},
			offices() {
				return this.$store.state.offices || {};
			},
			getMergeCandidate() {
				return this.$store.getters.generateCandidateObject;
			},
			donations() {
				return this.$store.state.donations;
			},
			hasMoreDonations() {
				return this.$store.state.hasMoreDonations;
			},
			tickets() {
				return this.$store.state.tickets;
			},
			hasMoreTickets() {
				return this.$store.state.hasMoreTickets;
			},
			goal() {
				return this.candidate.raising_goal;
			},
			candidate() {
				return this.getMergeCandidate.candidate;
			},
			donationSources() {
				let opacity = 0;
				return this.$store.state.donationSources
					.map((x) => {
						const y = x;
						y.opacity = opacity;
						opacity += 0.25;
						return y;
					});
			},
			totalAmount() {
				return this.donationSources.length
					? this.donationSources.reduce(
						(accumulator, currentValue) =>
							accumulator + currentValue.total_donated
						, 0,
					)
					: (this.candidate.total_donated || 0);
			},
			totalDonors() {
				return this.donationSources.length
					? this.donationSources.reduce(
						(accumulator, currentValue) =>
							accumulator + Number.parseInt(currentValue.people_donated || 0, 10)
						, 0,
					)
					: Number.parseInt(this.candidate.people_donated || 0, 10);
			},
		},
		created() {
			window.addEventListener('message', this.receiveMessage, false);
		},
		mounted() {
			this.getCandidateNameUrl();
			this.$store.dispatch('getOffices');
			this.handleSession();
			this.getReferral();
		},
		destroyed() {
			window.removeEventListener('message', this.receiveMessage);
		},
		methods: {
			getCandidateNameUrl() {
				this.$store.dispatch('getCandidate', this.$route.params.candidate)
					.then((response) => {
						//this.$store.dispatch('getCandidateProjects', response.id);
						const videoUrl = this.candidate.video_url;
						const youtubeMatch = videoUrl.match(/(?:[?&]vi?=|\/embed\/|\/\d\d?\/|\/vi?\/|https?:\/\/(?:www\.)?youtu\.be\/)([^&\n?#]+)/);
						if (youtubeMatch && youtubeMatch[1].length == 11 ) {
							this.videoId = youtubeMatch[1];
						} else {
							// TODO Throw error.
						}

						this.$emit('updateHead');

						this.activeSection = this.candidate.projects.length
							? 'projetos'
							: 'sobre';

						if (this.candidate.google_analytics) {
							ga('create', this.candidate.google_analytics, 'auto', 'VTcandidate');
							ga('VTcandidate.send', 'pageview');
						}
					})
					.catch(err => {
						if (err.status === 403) {
							this.errorMessage = 'Candidato desabilitado';
						} else if (err.status === 404) {
							this.errorMessage = 'Página não encontrada';
						} else if (err.status === 502) {
							this.errorMessage = 'Página em manutenção. Tente novamente em alguns minutos.';
						} else if (err.status === 429) {
							this.errorMessage = 'O seu IP acessou nosso serviço numa frequência maior que a esperada. Tente novamente em alguns segundos.';
						} else {
							this.errorMessage = err.message || err.name || err.data[0].message || '';
						}

						this.status = false;
					});

					this.$nextTick(function() {
						window.prerenderReady = true;
					});
			},
			handleSession() {
				if(window.localStorage) {
					const tokenName = window.location.host === 'votolegal.com.br' || window.location.host === 'votolegal.com'
						? 'prod_vlm_token'
						: 'dev_vlm_token';
					const token = localStorage.getItem(tokenName);
					if (token !== null) {
						this.$store.dispatch('ADD_TOKEN', token);
					} else {
						this.$store.dispatch('GET_TOKEN')
							.then((res) => {
								localStorage.setItem(tokenName, res.data.device_authorization_token_id);
							});
					}
				}
			},
			getReferral() {
				const referral = getQueryString(window.location.search).ref;

				if(referral) {
					this.$store.dispatch('ADD_REFERRAL', referral);
				}
			},
			changeSection(section) {
				this.activeSection = section;
			},
			getDonationsList() {
				this.$store.dispatch('getCandidateDonations', this.getMergeCandidate.candidate.id);
			},
			getTicketsList() {
				this.$store.dispatch('getCandidateTickets', this.getMergeCandidate.candidate.id);
			},
			FormatFixedBRL(amount) {
				let formated = `${(amount / 100).toFixed(2)}`;

				return formated
					.substring(0, formated.length - 2)
					.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.")
					.replace(/\.+$/, '');
			},
			getPlatformName(platformCNPJ) {
				return this.$store.state.donationPlatforms
					.filter(x => x.cnpj === platformCNPJ)
					.map(x => x.name)[0] || platformCNPJ;
			},
			porcentage(amount = this.totalAmount) {
				return Math.round((parseFloat(amount) * 100) / Math.max(this.totalAmount, this.goal));
			},
			progressBarStyle(source) {
				return {
					backgroundImage: `linear-gradient(rgba(0, 0, 0, ${source.opacity}), rgba(0, 0, 0, ${source.opacity}))`,
					width: `${this.porcentage(source.total_donated)}%`,
				};
			},

			receiveMessage(event) {
				if (
					event.origin.indexOf('participe.votolegal.com') === -1
					&& event.origin.indexOf('//dev') === -1
					&& event.origin.indexOf('//localhost') === -1
					&& event.origin.indexOf('.local') === -1
					&& event.origin.indexOf('//192.168.') === -1
				)	{
					return;
				}

				if (!event.data.theme) return;
				this.$refs.candidateRoot.className = event.data.theme;
			}
		},
		head: {
			title() {
				return {
					inner: this.candidate.popular_name || this.$route.params.candidate,
					complement: 'Voto Legal',
				};
			},
			meta() {
				return [
					{
						id: 'description',
						name: 'description',
						content: this.candidate.description || this.defaultDescription,
					},
					{
						id: 'og:locale',
						property: 'og:locale',
						content: 'pt_BR',
					},
					{
						id: 'fb:admins',
						property: 'fb:admins',
						content: '1063503096997131',
					},
					{
						id: 'og:type',
						property: 'og:type',
						content: 'website',
					},
					{
						id: 'twitter:card',
						name: 'twitter:card',
						content: 'summary',
					},
					{
						id: 'twitter:creator',
						name: 'twitter:creator',
						content: '@appcivico',
					},
					{
						id: 'twitter:description',
						itemprop: 'description',
						name: 'twitter:description',
						property: 'og:description',
						content: this.candidate.description || this.defaultDescription,
					},
					{
						id: 'image',
						itemprop: 'image',
						name: 'image',
						content: this.candidate.picture || '',
					},
					{
						id: 'twitter:title',
						itemprop: 'name',
						name: 'twitter:title',
						property: 'og:title',
						content: `Eu apoio ${this.candidate.popular_name || this.$route.params.candidate}! Apoie você também!`,
					},
					{
						id: 'twitter:url',
						name: 'twitter:url',
						property: 'og:url',
						content: `https://votolegal.com.br/${this.candidate.slug}`,
					},
					{
						id: 'twitter:site',
						name: 'twitter:site',
						content: this.candidate.twitter_profile || '',
					},
					{
						id: 'og:image',
						name: 'twitter:image:src',
						property: 'og:image',
						content: this.candidate.picture || '',
					},
				];
			},
		},
	}
</script>

<style src="../../../candidate-styles/index.scss" lang="scss"></style>



// WEBPACK FOOTER //
// _source/javascript/components/candidate/candidate.vue